<script setup lang="ts">
import { useFormValidation } from '../../../../composables/useFormValidation'
import { required } from '../../../../utils/validation'
import TextInput from '../../../../components/ui/form/TextInput.vue'
import CheckInput from '../../../../components/ui/form/CheckInput.vue'
import { api } from '../../../../api/Api'
import Toast from '../../../../services/Toast'
import { Permission } from '../../../../types/auth'
import { onMounted, ref, watch } from 'vue'
import ModalFormTemplate from '../../../../components/ui/Templates/ModalFormTemplate.vue'
import TextAreaInput from '../../../../components/ui/form/TextAreaInput.vue'
import { Module } from '../../../../types/models'

const props = defineProps<{
  data?: any | null
  mode: 'create' | 'edit' | 'view' | 'showQuickView' | 'toggleStatus'
  title: string | null
  endPoint: string,
  permissions?: Permission[],
  listCreateName?: string
}>()

const emit = defineEmits<{
  (e: 'save', data: any): void
  (e: 'close'): void
}>()

const { fields, formData, setFieldRules, validateForm, resetForm, setFormData } = useFormValidation({
  name: props.data?.name || props.listCreateName || "",
    description: props.data?.description || "",
    active: props.data?.active || true,
    uid: props.data?.uid || "",
    modules: props.data?.modules || [],
  });

setFieldRules("description", [required("Description is required")]);
setFieldRules("name", [required("Name is required")]);


const handleSubmit = async (close: boolean, newRecord: boolean) => {
  console.log(modules.value)
  console.log(expandedGroups.value)
  if (validateForm()) {
    formData.value.modules = modules.value;
    const action = formData.value.uid != '' ? 'update' : 'create'
    const response = await api.post((props.endPoint.slice(-1) == '/' ? props.endPoint : props.endPoint + '/') + action, formData.value)
    if (response.success == true) {
      Toast.show('saved successfully')
      emit('save', response.data)
      if (close) {
        emit('close')
      } else if (newRecord) {
        resetForm()
      } else {
        setFormData(response.data)
      }
    }
  }
}

const isReadOnly = ref(true);
onMounted(() => {
  isReadOnly.value = props.mode == 'view'
  if (props.mode == 'create') {
      resetForm()
    } else if (props.data) {
      setFormData(props.data)
    }
})


const modules = ref<Module[]>([]);
const activeTab = ref(0)
const expandedGroups = ref<number[]>([])

const toggleGroup = (groupId: number) => {
  const index = expandedGroups.value.indexOf(groupId)
  if (index === -1) {
    expandedGroups.value.push(groupId)
  } else {
    expandedGroups.value.splice(index, 1)
  }
}

const isGroupExpanded = (groupId: number) => {
  return expandedGroups.value.includes(groupId)
}

const handlePermissionChange = (permission: Permission) => {
  console.log(isReadOnly.value)
  if (!isReadOnly.value) {
    permission.allowed = !permission.allowed
  }
  console.log(permission)
  console.log(modules.value)
  console.log(expandedGroups.value)
}

const getModules = async (id: number) => {
  const apiData = {
    id: id,
  };
  const response = await api.post((props.endPoint.slice(-1) == '/' ? props.endPoint : props.endPoint + '/') + 'modules', apiData);
  if (response.success == true) {
    modules.value = response.data;
    if (modules.value[0]) {
      expandedGroups.value = modules.value[0].groups.map(group => group.id)
    }
  }
};
watch(() => props.data, () => {
  if (props.data) {
    getModules(props.data.id)
  }
})

watch(activeTab, (newTabIndex) => {
  if (modules.value[newTabIndex]) {
    expandedGroups.value = modules.value[newTabIndex].groups.map(group => group.id)
  }
})

onMounted(() => {
  if (props.data) {
    getModules(props.data.uid)
  } else {
    getModules(0)
  }
})


</script>
<template>
  <ModalFormTemplate :data="data" :mode="mode" :title="title" :endPoint="endPoint" :perm="permissions"
    :listCreateName="listCreateName" :tabList="[]" :formData="formData" @save="handleSubmit"
    @close="$emit('close')">
    <template #form>
      <div class="grid md:grid-cols-2 gap-6">
        <TextInput v-model="fields.name.value" label="Name" placeholder="Enter Name" :readonly="isReadOnly"
          :error="fields.name.touched ? fields.name.error : null" @blur="fields.name.touched = true" />

        <TextAreaInput v-model="fields.description.value" label="Description" :rows="5" :readonly="isReadOnly"
          :error="fields.description.touched ? fields.description.error : null"
          @blur="fields.description.touched = true" />

        <CheckInput v-model="fields.active.value" :readonly="isReadOnly" label="Active"
          :error="fields.active.touched ? fields.active.error : null" @blur="fields.active.touched = true" />
      </div>
      <div class="p-4 bg-gray-50 dark:bg-gray-900 rounded-lg">
        <div class="">
          <span class="text-2xl font-medium text-gray-900 dark:text-white">Permissions</span>
          <div>
            <div class="flex">
              <!-- Vertical Tabs -->
              <div class="w-64 border-r border-gray-100 dark:border-gray-800">
                <div class="p-4">
                  <button v-for="(area, index) in modules" :key="area.id" @click="activeTab = index"
                    class="w-full px-4 py-3 text-left rounded-lg mb-2 transition-colors flex items-center gap-3" :class="[
                      activeTab === index
                        ? 'bg-blue-50 dark:bg-blue-900/50 text-blue-600 dark:text-blue-400'
                        : 'hover:bg-gray-50 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300'
                    ]">
                    <span class="text-xl">{{ area.name }}</span>
                  </button>
                </div>
              </div>

              <!-- Tab Content -->
              <div class="flex-1 p-6">
                <template v-if="modules[activeTab]">
                  <div class="mb-6">
                    <h2 class="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                      {{ modules[activeTab].name }}
                    </h2>
                    <p class="text-gray-600 dark:text-gray-400">
                      {{ modules[activeTab].intro }}
                    </p>
                  </div>

                  <!-- Groups -->
                  <div class="space-y-6">
                    <div v-for="group in modules[activeTab].groups" :key="group.id"
                      class="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                      <!-- Group Header -->
                      <button @click="toggleGroup(group.id)"
                        class="w-full px-6 py-4 bg-gray-50 dark:bg-gray-800 flex items-center justify-between hover:bg-gray-100 dark:hover:bg-gray-700">
                        <div class="flex items-center gap-3">
                          <span class="text-xl">{{ group.name }}</span>
                        </div>
                        <span class="text-gray-500 transition-transform"
                          :class="{ 'rotate-180': isGroupExpanded(group.id) }">
                          ▼
                        </span>
                      </button>

                      <!-- Group Content -->
                      <div v-if="isGroupExpanded(group.id)" class="p-6 space-y-6">
                        <div v-for="module in group.modules" :key="module.id"
                          class="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
                          <h4 class="font-medium text-gray-900 dark:text-white mb-4">{{ module.name }}</h4>

                          <!-- Permissions Grid -->
                          <div class="grid grid-cols-2 gap-4">
                            <label v-for="permission in module.permissions" :key="permission.id"
                              class="flex items-center gap-2">
                              <input type="checkbox" :checked="permission.allowed"
                              v-if="isReadOnly"
                              onclick="return false;"
                                class="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" />
                              <input type="checkbox" :checked="permission.allowed"
                              v-else
                                @change="handlePermissionChange(permission)"
                                class="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" />
                              <span class="text-sm text-gray-700 dark:text-gray-300">
                                {{ permission.name }}
                              </span>
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </template>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
  </ModalFormTemplate>
</template>